import { Builder } from '@builder.io/react';
import cn from 'classnames';
import parse from 'html-react-parser';
import { FC, useEffect, useId, useState } from 'react';

import { Button, Checkbox } from '@catawiki/cata-style';

import { COLOR_NAME_WHITE } from '@/shared/constants/colors';
import { backgroundColorInput } from '@/shared/inputs';
import { getBuilderSpacingClass } from '@/shared/spacing';
import {
  cleanBuilderParagraphInput,
  cleanBuilderStringInput,
  getBuilderString,
  isBuilderStringPresent,
} from '@/shared/text';
import { BuilderString, SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';

import sharedStyle from '../shared.module.scss';
import styles from './Checklist.module.scss';

export interface ChecklistItem {
  label: BuilderString;
  description?: BuilderString;
}

export type ChecklistProps = SharedBuilderComponent & {
  title: BuilderString;
  introduction?: BuilderString;
  items: ChecklistItem[];
  printButtonLabel: BuilderString;
  locale?: string;
};

export const Checklist: FC<ChecklistProps> = ({
  title,
  introduction,
  items = [],
  printButtonLabel,
  locale,
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
}) => {
  const generatedId = useId();
  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);
  const blockLocale = locale || builderState?.context.locale || 'en';
  const titleText = cleanBuilderStringInput(
    getBuilderString(title, blockLocale),
    true
  );
  const introductionHtml = cleanBuilderParagraphInput(
    getBuilderString(introduction, blockLocale),
    { allowLists: true }
  );
  const printLabel = cleanBuilderStringInput(
    getBuilderString(printButtonLabel, blockLocale),
    true
  );

  useEffect(() => setCheckedItems([]), [items]);

  return (
    <section
      {...attributes}
      data-checklist
      data-testid="checklist"
      aria-labelledby={`${generatedId}-title`}
      className={cn(
        sharedStyle.section,
        styles.root,
        getBuilderIOClassName(attributes),
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <div className="u-wrapper">
        <div className={styles.content}>
          <div className={styles.header}>
            <div>
              <h2
                id={`${generatedId}-title`}
                className={cn('tw:text-h3 tw:mb-m', styles.title)}
              >
                {titleText}
              </h2>
              {isBuilderStringPresent(introduction, blockLocale) && (
                <div className="tw:text-body tw:text-neutral-secondary">
                  {parse(introductionHtml)}
                </div>
              )}
            </div>
            <Button
              className={styles.printButton}
              type="button"
              variant="secondary"
              onClick={() => window.print()}
            >
              {printLabel}
            </Button>
          </div>

          <p
            className={cn(
              'tw:text-label-l tw:text-neutral-secondary tw:mb-m',
              styles.progress
            )}
            role="status"
          >
            {checkedItems.length} of {items.length} complete
          </p>

          <ol className={styles.items}>
            {items.map((item, index) => {
              const descriptionHtml = cleanBuilderParagraphInput(
                getBuilderString(item.description, blockLocale),
                { allowLists: true }
              );
              return (
                <li
                  className={cn(
                    'tw:border-t tw:border-neutral-secondary tw:py-l',
                    styles.item
                  )}
                  key={index}
                >
                  <Checkbox
                    id={`${generatedId}-item-${index}`}
                    checked={checkedItems.includes(index)}
                    className={styles.checkbox}
                    onToggle={(checked) =>
                      setCheckedItems((current) =>
                        checked
                          ? [...current, index]
                          : current.filter(
                              (checkedIndex) => checkedIndex !== index
                            )
                      )
                    }
                  >
                    <span className={cn('tw:text-h5', styles.itemLabel)}>
                      {cleanBuilderStringInput(
                        getBuilderString(item.label, blockLocale),
                        true
                      )}
                    </span>
                  </Checkbox>
                  {isBuilderStringPresent(item.description, blockLocale) && (
                    <div
                      className={cn(
                        'tw:text-body tw:text-neutral-secondary',
                        styles.description
                      )}
                    >
                      {parse(descriptionHtml)}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
};

export const NAME = 'Checklist';

export function dataFetcher(locale: string) {
  return async () => ({ locale });
}

export function componentRegister() {
  Builder.registerComponent(Checklist, {
    name: NAME,
    inputs: [
      {
        name: 'title',
        friendlyName: 'Title',
        type: 'CatawikiPlainEditor',
        defaultValue: 'Before you go live',
        localized: true,
      },
      {
        name: 'introduction',
        friendlyName: 'Introduction',
        type: 'CatawikiEditor',
        defaultValue: 'Run through these checks before the broadcast starts.',
        localized: true,
      },
      {
        name: 'items',
        friendlyName: 'Checklist items',
        type: 'list',
        defaultValue: [
          {
            label: 'Confirm your equipment is ready.',
            description: 'Check your camera, microphone, and lighting.',
          },
        ],
        subFields: [
          {
            name: 'label',
            friendlyName: 'Checklist item',
            type: 'CatawikiPlainEditor',
            required: true,
            localized: true,
          },
          {
            name: 'description',
            friendlyName: 'Description',
            type: 'CatawikiEditor',
            localized: true,
          },
        ],
      },
      {
        name: 'printButtonLabel',
        friendlyName: 'Print button label',
        type: 'CatawikiPlainEditor',
        defaultValue: 'Print / Save PDF',
        localized: true,
      },
      backgroundColorInput(COLOR_NAME_WHITE),
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
});
